import React, { createContext, useContext, useReducer, useCallback } from 'react';

// Initial state
const initialState = {
  mapType: 'personal', // 'personal' or 'club'
  features: [],
  mapView: {
    center: [-98.5795, 39.8283],
    zoom: 4,
    bounds: null
  },
  loading: false,
  error: null,
  drawingMode: false,
  selectedFeature: null,
  clubId: null,
  colorScheme: 'default'
};

// Map context state with default value
const MapContext = createContext(initialState);

// Action types
const MAP_ACTIONS = {
  SET_MAP_TYPE: 'SET_MAP_TYPE',
  SET_FEATURES: 'SET_FEATURES',
  ADD_FEATURE: 'ADD_FEATURE',
  UPDATE_FEATURE: 'UPDATE_FEATURE',
  DELETE_FEATURE: 'DELETE_FEATURE',
  SET_MAP_VIEW: 'SET_MAP_VIEW',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_DRAWING_MODE: 'SET_DRAWING_MODE',
  SET_SELECTED_FEATURE: 'SET_SELECTED_FEATURE',
  SET_COLOR_SCHEME: 'SET_COLOR_SCHEME'
};

// Reducer
function mapReducer(state, action) {
  switch (action.type) {
    case MAP_ACTIONS.SET_MAP_TYPE:
      return {
        ...state,
        mapType: action.payload.mapType,
        clubId: action.payload.clubId || null
      };
      
    case MAP_ACTIONS.SET_FEATURES:
      return {
        ...state,
        features: action.payload
      };
      
    case MAP_ACTIONS.ADD_FEATURE:
      return {
        ...state,
        features: [...state.features, action.payload]
      };
      
    case MAP_ACTIONS.UPDATE_FEATURE:
      return {
        ...state,
        features: state.features.map(feature => 
          feature.id === action.payload.id ? action.payload : feature
        )
      };
      
    case MAP_ACTIONS.DELETE_FEATURE:
      return {
        ...state,
        features: state.features.filter(feature => feature.id !== action.payload)
      };
      
    case MAP_ACTIONS.SET_MAP_VIEW:
      return {
        ...state,
        mapView: { ...state.mapView, ...action.payload }
      };
      
    case MAP_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
      
    case MAP_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload
      };
      
    case MAP_ACTIONS.SET_DRAWING_MODE:
      return {
        ...state,
        drawingMode: action.payload
      };
      
    case MAP_ACTIONS.SET_SELECTED_FEATURE:
      return {
        ...state,
        selectedFeature: action.payload
      };
      
    case MAP_ACTIONS.SET_COLOR_SCHEME:
      return {
        ...state,
        colorScheme: action.payload
      };
      
    default:
      return state;
  }
}

// Map provider component
export function MapProvider({ children }) {
  const [state, dispatch] = useReducer(mapReducer, initialState);

  // Actions
  const setMapType = useCallback((mapType, clubId = null) => {
    dispatch({ type: MAP_ACTIONS.SET_MAP_TYPE, payload: { mapType, clubId } });
  }, []);

  const setFeatures = useCallback((features) => {
    dispatch({ type: MAP_ACTIONS.SET_FEATURES, payload: features });
  }, []);

  const addFeature = useCallback((feature) => {
    dispatch({ type: MAP_ACTIONS.ADD_FEATURE, payload: feature });
  }, []);

  const updateFeature = useCallback((feature) => {
    dispatch({ type: MAP_ACTIONS.UPDATE_FEATURE, payload: feature });
  }, []);

  const deleteFeature = useCallback((featureId) => {
    dispatch({ type: MAP_ACTIONS.DELETE_FEATURE, payload: featureId });
  }, []);

  const setMapView = useCallback((view) => {
    dispatch({ type: MAP_ACTIONS.SET_MAP_VIEW, payload: view });
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: MAP_ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: MAP_ACTIONS.SET_ERROR, payload: error });
  }, []);

  const setDrawingMode = useCallback((mode) => {
    dispatch({ type: MAP_ACTIONS.SET_DRAWING_MODE, payload: mode });
  }, []);

  const setSelectedFeature = useCallback((feature) => {
    dispatch({ type: MAP_ACTIONS.SET_SELECTED_FEATURE, payload: feature });
  }, []);

  const setColorScheme = useCallback((scheme) => {
    dispatch({ type: MAP_ACTIONS.SET_COLOR_SCHEME, payload: scheme });
  }, []);

  const value = {
    ...state,
    setMapType,
    setFeatures,
    addFeature,
    updateFeature,
    deleteFeature,
    setMapView,
    setLoading,
    setError,
    setDrawingMode,
    setSelectedFeature,
    setColorScheme
  };

  return (
    <MapContext.Provider value={value}>
      {children}
    </MapContext.Provider>
  );
}

// Hook to use map context
export function useMap() {
  const context = useContext(MapContext);
  if (!context) {
    throw new Error('useMap must be used within a MapProvider');
  }
  return context;
}
